<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useClipboard } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import Api from 'dashboard/api/customers';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import Button from 'dashboard/components-next/button/Button.vue';
import CustomerForm from './CustomerForm.vue';
import ReferralForm from './ReferralForm.vue';
import QualifyDialog from './QualifyDialog.vue';

const { t } = useI18n();
const route = useRoute();
const customer = ref(null);
const form = ref(null);
const referralForm = ref(null);
const qualifyDialog = ref(null);
const error = ref('');
const { copy } = useClipboard();
const { run, isPending } = useAbortableRequest();
const load = async () => {
  customer.value = null;
  error.value = '';
  try {
    const response = await run(signal =>
      window.axios.get(`${Api.url}/${route.params.customerId}`, { signal })
    );
    if (response) customer.value = response.data;
  } catch (e) {
    error.value = e.response?.data?.error || t('CUSTOMERS.ERROR');
  }
};
watch(() => [route.params.accountId, route.params.customerId], load, {
  immediate: true,
});
const saved = value => {
  customer.value = value;
  useAlert(t('CUSTOMERS.SAVED'));
};
const shareUrl = computed(
  () =>
    `https://wa.me/?text=${encodeURIComponent(t('CUSTOMERS.SHARE_TEXT', { link: customer.value?.share_link }))}`
);
const copyLink = async () => {
  try {
    await copy(customer.value.share_link);
    useAlert(t('CUSTOMERS.COPIED'));
  } catch (e) {
    useAlert(t('CUSTOMERS.COPY_ERROR'));
  }
};
</script>

<template>
  <section
    class="h-full w-full overflow-auto bg-n-background text-n-slate-12"
    :aria-busy="isPending"
  >
    <header
      class="flex items-center justify-between border-b border-n-weak px-8 py-5"
    >
      <RouterLink
        :to="{
          name: 'customers_index',
          params: { accountId: route.params.accountId },
        }"
        class="text-n-blue-11"
        >{{ t('CUSTOMERS.BACK') }}</RouterLink
      >
      <Button
        v-if="customer"
        :label="t('CUSTOMERS.EDIT')"
        variant="outline"
        @click="form.open(customer)"
      />
    </header>
    <p v-if="error" role="alert" class="p-8 text-n-ruby-11">{{ error }}</p>
    <div v-if="customer" class="mx-auto flex max-w-5xl flex-col gap-6 p-8">
      <div>
        <h1 class="text-heading-1">{{ customer.name }}</h1>
        <p class="mt-2 text-n-slate-11">
          {{ t(`CUSTOMERS.TYPES.${customer.customer_type}`) }}
        </p>
      </div>
      <section class="rounded-xl border border-n-weak bg-n-surface-1 p-6">
        <h2 class="text-heading-3">{{ t('CUSTOMERS.PROFILE') }}</h2>
        <dl class="mt-5 grid gap-5 sm:grid-cols-3">
          <div
            v-for="field in [
              { key: 'email', label: 'EMAIL' },
              { key: 'phone_number', label: 'PHONE' },
              { key: 'expires_on', label: 'EXPIRY' },
            ]"
            :key="field.key"
          >
            <dt class="text-sm text-n-slate-11">
              {{ t(`CUSTOMERS.${field.label}`) }}
            </dt>
            <dd class="mt-1 font-medium">
              {{ customer[field.key] || t('CUSTOMERS.NOT_SET') }}
            </dd>
          </div>
        </dl>
        <RouterLink
          :to="{
            name: 'contacts_edit',
            params: {
              accountId: route.params.accountId,
              contactId: customer.contact_id,
            },
          }"
          class="mt-5 inline-block text-n-blue-11"
          >{{ t('CUSTOMERS.CHAT_HISTORY') }}</RouterLink
        >
      </section>
      <section
        v-if="customer.customer_type === 'existing_client'"
        class="rounded-xl border border-n-weak bg-n-surface-1 p-6"
      >
        <h2 class="text-heading-3">{{ t('CUSTOMERS.MY_REFERRAL') }}</h2>
        <p class="mt-4 text-sm text-n-slate-11">
          {{ t('CUSTOMERS.REFERRAL_ID') }}
        </p>
        <p class="mt-1 font-mono text-xl">{{ customer.referral_code }}</p>
        <a
          :href="customer.share_link"
          target="_blank"
          rel="noopener noreferrer"
          class="mt-4 block break-all text-n-blue-11"
          >{{ customer.share_link }}</a
        >
        <div class="mt-4 flex flex-wrap items-center gap-3">
          <Button
            :label="t('CUSTOMERS.COPY')"
            variant="outline"
            icon="i-lucide-copy"
            @click="copyLink"
          /><a
            :href="shareUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="rounded-lg bg-n-brand px-4 py-2 text-white"
            >{{ t('CUSTOMERS.SHARE_WHATSAPP') }}</a
          >
        </div>
        <p class="mt-5 text-n-slate-11">
          {{
            t('CUSTOMERS.SUCCESS_COUNT', {
              count: customer.successful_referrals,
            })
          }}
          · {{ t('CUSTOMERS.MONTHS', { count: customer.reward_months }) }}
        </p>
      </section>
      <section class="rounded-xl border border-n-weak bg-n-surface-1 p-6">
        <div class="flex items-center justify-between">
          <h2 class="text-heading-3">{{ t('CUSTOMERS.REFERRAL_REWARDS') }}</h2>
          <Button
            v-if="
              !customer.first_paid_at &&
              customer.referral?.status !== 'qualified'
            "
            :label="t('CUSTOMERS.SET_REFERRER')"
            variant="ghost"
            @click="referralForm.open(customer)"
          />
        </div>
        <p class="mt-4">
          {{ t('CUSTOMERS.REFERRED_BY') }}:
          <RouterLink
            v-if="customer.referral"
            :to="{
              name: 'customers_show',
              params: {
                accountId: route.params.accountId,
                customerId: customer.referral.referrer_id,
              },
            }"
            class="text-n-blue-11"
            >{{ customer.referral.referrer_name }}</RouterLink
          ><span v-else>{{ t('CUSTOMERS.NOT_SET') }}</span>
        </p>
        <p class="mt-2 text-n-slate-11">
          {{ t(`CUSTOMERS.STATUSES.${customer.referral?.status || 'none'}`) }}
        </p>
        <Button
          v-if="
            customer.referral?.status === 'pending' && !customer.first_paid_at
          "
          class="mt-5"
          :label="t('CUSTOMERS.QUALIFY')"
          @click="qualifyDialog.open(customer)"
        />
        <p
          v-if="customer.referral?.qualified_at"
          class="mt-3 text-sm text-n-slate-11"
        >
          {{
            t('CUSTOMERS.QUALIFIED_BY', {
              name: customer.referral.qualified_by,
              date: customer.referral.qualified_at,
            })
          }}
        </p>
        <div
          v-for="reward in customer.referral?.rewards || []"
          :key="reward.id"
          class="mt-4 rounded-lg bg-n-background p-4"
        >
          <p class="font-medium">
            {{ reward.customer_name }} ·
            {{ t('CUSTOMERS.MONTHS', { count: reward.months }) }}
          </p>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ reward.previous_expires_on }} → {{ reward.expires_on }}
          </p>
        </div>
        <details v-if="customer.rewards.length" class="mt-5">
          <summary class="cursor-pointer text-n-blue-11">
            {{ t('CUSTOMERS.REWARD_HISTORY') }}
          </summary>
          <div
            v-for="reward in customer.rewards"
            :key="reward.id"
            class="mt-3 border-t border-n-weak pt-3 text-sm"
          >
            {{ t('CUSTOMERS.MONTHS', { count: reward.months }) }} ·
            {{ reward.previous_expires_on }} → {{ reward.expires_on }}
            <p class="mt-1 text-n-slate-11">
              {{ reward.created_by }} · {{ reward.created_at }}
            </p>
          </div>
        </details>
        <details v-if="customer.referral?.corrections?.length" class="mt-5">
          <summary class="cursor-pointer text-n-blue-11">
            {{ t('CUSTOMERS.CORRECTIONS') }}
          </summary>
          <p
            v-for="(correction, index) in customer.referral.corrections"
            :key="index"
            class="mt-2 text-sm text-n-slate-11"
          >
            {{ correction.reason }} · {{ correction.at }}
          </p>
        </details>
      </section>
    </div>
    <CustomerForm ref="form" @saved="saved" /><ReferralForm
      ref="referralForm"
      @saved="saved"
    /><QualifyDialog ref="qualifyDialog" @saved="saved" />
  </section>
</template>
